import { useEffect, useState } from "react";
import React from "react";
import { RefreshCw, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminAuditLog, adminListClientErrors, adminClearClientErrors } from "../../lib/api";

export function AuditLogTab({ pw }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setRows(await adminAuditLog(pw, 200)); } catch { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, [pw]);
  return (
    <div className="space-y-6" data-testid="audit-log-tab-wrap">
      {/* iter92 — Client-side JS errors captured from the admin UI. */}
      <ClientErrorsCard pw={pw} />

      <div className="surface-card overflow-x-auto" data-testid="audit-log-tab">
      <div className="p-4 flex items-center justify-between flex-wrap gap-3 border-b border-[#222]">
        <div>
          <h2 className="font-display text-2xl uppercase">Audit Log</h2>
          <p className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">EVERY ADMIN WRITE ACTION · NEWEST FIRST</p>
        </div>
        <button onClick={load} disabled={busy} data-testid="audit-log-refresh"
          className="btn-tc-secondary inline-flex items-center gap-2 text-xs disabled:opacity-50">
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {busy ? "Loading…" : "Refresh"}
        </button>
      </div>
      <table className="w-full text-left">
        <thead className="bg-[#050505] border-b border-[#222]">
          <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
            <th className="p-4">WHEN</th><th className="p-4">ACTION</th><th className="p-4">TARGET</th><th className="p-4">DETAILS</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-zinc-500">No admin actions logged yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-[#222] hover:bg-[#0d0d0d]" data-testid={`audit-row-${r.id}`}>
              <td className="p-4 font-mono text-xs text-zinc-300">{new Date(r.at).toLocaleString()}</td>
              <td className="p-4 font-ui font-bold uppercase text-sm">{r.action}</td>
              <td className="p-4 font-mono text-xs">
                <span className="text-zinc-400">{r.target_type}</span>
                {r.target_id && <span className="text-zinc-500"> · {r.target_id.slice(0, 20)}</span>}
              </td>
              <td className="p-4 font-mono text-[10px] text-zinc-500 max-w-md truncate">{JSON.stringify(r.details || {})}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

/**
 * iter92 — Client-side error viewer. Reads from
 * `GET /api/admin/client-errors` (populated by `lib/errorReporter.js`).
 * Displays the last N unhandled errors + promise rejections, grouped
 * by (message + route + IP) with a `count` for repeats within a 5-min
 * window.
 */
function ClientErrorsCard({ pw }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const load = async () => {
    setBusy(true);
    try {
      const d = await adminListClientErrors(pw, 50);
      setRows(d.rows || []);
      setTotal(d.total || 0);
    } catch { /* ignore — this is the FRONTEND error viewer; failing silently is exactly what we're avoiding elsewhere */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, [pw]);
  const clear = async () => {
    if (!window.confirm(`Delete all ${total} client-error rows?`)) return;
    try {
      const r = await adminClearClientErrors(pw);
      toast.success(`Removed ${r.removed} error rows`);
      load();
    } catch { toast.error("Clear failed"); }
  };
  return (
    <div className="surface-card overflow-x-auto" data-testid="client-errors-card">
      <div className="p-4 flex items-center justify-between flex-wrap gap-3 border-b border-[#222]">
        <div>
          <h2 className="font-display text-2xl uppercase flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-400" /> Client Errors
          </h2>
          <p className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">
            UNCAUGHT JS ERRORS + PAYMENT FAILURES · {total} TOTAL · DEDUPED WITHIN 5 MIN
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy} data-testid="client-errors-refresh"
            className="btn-tc-secondary inline-flex items-center gap-2 text-xs disabled:opacity-50">
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
          {rows.length > 0 && (
            <button onClick={clear} data-testid="client-errors-clear"
              className="border border-red-900 hover:border-red-500 text-red-400 hover:text-red-200 px-3 py-1.5 font-mono text-[10px] tracking-widest inline-flex items-center gap-1.5">
              <Trash2 size={12} /> CLEAR ALL
            </button>
          )}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center font-mono text-xs text-zinc-500 tracking-widest">
          {busy ? "LOADING…" : "NO CLIENT ERRORS · ADMIN UI IS HEALTHY 🎉"}
        </div>
      ) : (
        <table className="w-full text-left">
          <thead className="bg-[#050505] border-b border-[#222]">
            <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
              <th className="p-3">LAST SEEN</th><th className="p-3">×</th><th className="p-3">ROUTE</th><th className="p-3">MESSAGE</th><th className="p-3">KIND</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <tr onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                    className="border-b border-[#222] hover:bg-[#0d0d0d] cursor-pointer"
                    data-testid={`client-error-row-${r.id}`}>
                  <td className="p-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                    {r.last_seen_at ? new Date(r.last_seen_at).toLocaleString(undefined, { hour12: true }) : "—"}
                  </td>
                  <td className="p-3 font-mono text-xs">
                    <span className={r.count > 1 ? "text-amber-400 font-bold" : "text-zinc-500"}>{r.count || 1}</span>
                  </td>
                  <td className="p-3 font-mono text-[10px] text-zinc-400">{r.route || "—"}</td>
                  <td className="p-3 font-mono text-[11px] text-red-300 max-w-lg truncate">{r.message}</td>
                  <td className="p-3 font-mono text-[10px] text-zinc-500 uppercase">{r.kind}</td>
                </tr>
                {expandedId === r.id && (r.stack || r.kind === "payment_failure") && (
                  <tr className="border-b border-[#222] bg-[#0a0a0a]">
                    <td colSpan={5} className="p-4">
                      {r.kind === "payment_failure" && (
                        <div className="mb-3 font-mono text-[11px] text-zinc-300 space-y-1" data-testid={`payment-failure-detail-${r.id}`}>
                          <div>ORDER: <span className="text-white">{r.order_number || "—"}</span> · <span className="text-white">${(r.order_total || 0).toFixed(2)}</span></div>
                          {r.customer_email && <div>CUSTOMER: <a href={`mailto:${r.customer_email}`} className="text-[var(--tc-red)] underline">{r.customer_email}</a></div>}
                          <div>SQUARE CODE: <span className="text-amber-300">{r.square_error_code || "—"}</span></div>
                          <div>SQUARE DETAIL: <span className="text-red-300">{r.square_error_detail || "—"}</span></div>
                          <div className="text-zinc-500">SESSION: {r.session_id || "—"}</div>
                        </div>
                      )}
                      {r.stack && <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap font-mono overflow-x-auto max-h-96">{r.stack}</pre>}
                      <div className="mt-2 font-mono text-[9px] text-zinc-600">
                        {r.source && <>SOURCE: {r.source}{r.line ? `:${r.line}` : ""}{r.column ? `:${r.column}` : ""} · </>}
                        USER-AGENT: {(r.user_agent || "").slice(0, 90)}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ===== REFUND DIALOG =====

export default AuditLogTab;
